import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar, CheckCircle2, XCircle, Clock, DollarSign,
  Building, AlertTriangle, ChevronDown, ChevronUp, Edit, Trash2,
  Plus, Video, MapPin, Truck
} from 'lucide-react';
import { BACKEND_URL } from '@/api';

const API = BACKEND_URL + '/api';
const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
  'Content-Type': 'application/json'
});

const STATUS_MAP = {
  payment_pending: { label: 'Payment Pending', cls: 'bg-yellow-600', icon: Clock },
  pending: { label: 'Pending Review', cls: 'bg-amber-500', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-green-600', icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-red-600', icon: XCircle },
};

const LOCATION_LABELS = {}; // Populated dynamically from DB

// ============== BOOKING MANAGEMENT ==============
const BookingManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [adjustForm, setAdjustForm] = useState({ id: null, amount: '', notes: '' });

  const loadData = useCallback(async () => {
    try {
      const [bRes, sRes] = await Promise.all([
        fetch(`${API}/admin/bookings${filter ? `?status=${filter}` : ''}`, { headers: getHeaders() }),
        fetch(`${API}/admin/bookings/stats`, { headers: getHeaders() }),
      ]);
      setBookings(await bRes.json());
      setStats(await sRes.json());
    } catch {}
  }, [filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await fetch(`${API}/admin/bookings/${id}/approve`, { method: 'PUT', headers: getHeaders() });
      loadData();
    } catch {}
    setActionLoading('');
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      await fetch(`${API}/admin/bookings/${id}/reject`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ reason: rejectReason })
      });
      setRejectReason('');
      loadData();
    } catch {}
    setActionLoading('');
  };

  const handleAdjust = async () => {
    if (!adjustForm.id || !adjustForm.amount) return;
    setActionLoading(adjustForm.id);
    try {
      await fetch(`${API}/admin/bookings/${adjustForm.id}/adjust`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ adjusted_total: parseFloat(adjustForm.amount), admin_notes: adjustForm.notes })
      });
      setAdjustForm({ id: null, amount: '', notes: '' });
      loadData();
    } catch {}
    setActionLoading('');
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-gray-700/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-white">{stats.total_bookings}</div>
            <div className="text-xs text-gray-400">Total</div>
          </div>
          {['pending', 'approved', 'rejected', 'payment_pending'].map(status => {
            const data = stats.by_status?.[status] || { count: 0, revenue: 0 };
            const style = STATUS_MAP[status];
            return (
              <div key={status} className="bg-gray-700/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">{data.count}</div>
                <div className="text-xs text-gray-400">{style.label}</div>
                <div className="text-xs text-green-400">${data.revenue?.toFixed(0)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['', 'pending', 'approved', 'rejected', 'payment_pending'].map(f => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'}
            className={filter === f ? 'bg-sky-500' : 'border-gray-600 text-gray-400'}
            onClick={() => setFilter(f)} data-testid={`filter-${f || 'all'}`}>
            {f ? STATUS_MAP[f]?.label : 'All'}
          </Button>
        ))}
      </div>

      {/* Bookings List */}
      <div className="space-y-3">
        {bookings.map(b => {
          const st = STATUS_MAP[b.status] || STATUS_MAP.pending;
          const StIcon = st.icon;
          const isExpanded = expandedId === b.id;
          return (
            <Card key={b.id} className="bg-gray-800/50 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge className={`${st.cls} text-white text-xs`}><StIcon className="w-3 h-3 mr-1" />{st.label}</Badge>
                      {b.is_estimate && <Badge className="bg-amber-600/50 text-amber-200 text-xs">Estimate</Badge>}
                      <Badge variant="outline" className="border-gray-600 text-gray-300 text-xs">{b.fleet_name}</Badge>
                    </div>
                    <h3 className="text-white font-semibold">{b.venue_name}</h3>
                    <div className="text-sm text-gray-400 mt-1">
                      <span>{b.client_name}</span>
                      {b.client_organization && <span className="text-gray-500"> — {b.client_organization}</span>}
                      <span className="ml-3 text-gray-500">{b.client_email}</span>
                    </div>
                    <div className="text-sm text-gray-400 flex gap-4 mt-1">
                      <span><Calendar className="w-3 h-3 inline mr-1" />{b.game_date} {b.game_time}</span>
                      <span>{b.sport}</span>
                    </div>
                  </div>
                  <div className="text-right ml-4 flex items-start gap-3">
                    <div>
                      <div className="text-xl font-bold text-green-400">${(b.adjusted_total || b.total).toFixed(2)}</div>
                      {b.adjusted_total && <div className="text-xs text-gray-500 line-through">${b.total.toFixed(2)}</div>}
                    </div>
                    <button onClick={() => setExpandedId(isExpanded ? null : b.id)} className="text-gray-400 hover:text-white">
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-700 space-y-3">
                    {/* Surcharges */}
                    {b.surcharges?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 font-medium mb-1">Surcharges:</p>
                        {b.surcharges.map((s, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-amber-400">{s.name}</span>
                            <span className="text-amber-400">+${s.amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Details */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-gray-500">Venue Address:</span> <span className="text-gray-300">{b.venue_address}</span></div>
                      <div><span className="text-gray-500">Elevated Area:</span> <span className={b.has_elevated_area ? 'text-green-400' : 'text-amber-400'}>{b.has_elevated_area ? 'Yes' : 'No'}</span></div>
                      {b.add_commentator && <div><span className="text-gray-500">Commentator:</span> <span className="text-purple-400">{b.commentator_name || 'Any'}</span></div>}
                      {b.notes && <div className="col-span-2"><span className="text-gray-500">Notes:</span> <span className="text-gray-300">{b.notes}</span></div>}
                      {b.admin_notes && <div className="col-span-2"><span className="text-gray-500">Admin Notes:</span> <span className="text-sky-300">{b.admin_notes}</span></div>}
                    </div>

                    {/* Actions */}
                    {b.status === 'pending' && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(b.id)}
                          disabled={actionLoading === b.id} data-testid={`approve-${b.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <div className="flex gap-2 flex-1">
                          <Input placeholder="Rejection reason..." value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            className="bg-gray-700 border-gray-600 text-white text-sm flex-1" />
                          <Button size="sm" variant="destructive" onClick={() => handleReject(b.id)}
                            disabled={actionLoading === b.id} data-testid={`reject-${b.id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                        <div className="flex gap-2 w-full mt-2">
                          <Input placeholder="Adjusted total" type="number" step="0.01"
                            value={adjustForm.id === b.id ? adjustForm.amount : ''}
                            onChange={e => setAdjustForm({ id: b.id, amount: e.target.value, notes: adjustForm.notes })}
                            className="bg-gray-700 border-gray-600 text-white text-sm w-32" />
                          <Input placeholder="Adjustment notes" value={adjustForm.id === b.id ? adjustForm.notes : ''}
                            onChange={e => setAdjustForm({ ...adjustForm, id: b.id, notes: e.target.value })}
                            className="bg-gray-700 border-gray-600 text-white text-sm flex-1" />
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black" onClick={handleAdjust}
                            disabled={adjustForm.id !== b.id || !adjustForm.amount} data-testid={`adjust-${b.id}`}>
                            <Edit className="w-4 h-4 mr-1" /> Adjust Price
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {bookings.length === 0 && (
          <p className="text-gray-500 text-center py-8">No bookings {filter ? `with status "${filter}"` : 'yet'}.</p>
        )}
      </div>
    </div>
  );
};

// ============== VENUE MANAGEMENT ==============
const VenueManagement = ({ locations }) => {
  const [venues, setVenues] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', sport: 'Soccer', pricing: {} });
  const [loading, setLoading] = useState(false);

  const loadVenues = async () => {
    const res = await fetch(`${API}/admin/venues`, { headers: getHeaders() });
    setVenues(await res.json());
  };

  useEffect(() => { loadVenues(); }, []);

  const locLabels = {};
  locations.forEach(l => { locLabels[l.slug] = l.name; });

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editId) {
        await fetch(`${API}/admin/venues/${editId}`, {
          method: 'PUT', headers: getHeaders(), body: JSON.stringify(form)
        });
      } else {
        await fetch(`${API}/admin/venues`, {
          method: 'POST', headers: getHeaders(), body: JSON.stringify(form)
        });
      }
      setShowAdd(false);
      setEditId(null);
      setForm({ name: '', address: '', sport: 'Soccer', pricing: {} });
      loadVenues();
    } catch {}
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this venue?')) return;
    await fetch(`${API}/admin/venues/${id}`, { method: 'DELETE', headers: getHeaders() });
    loadVenues();
  };

  const startEdit = (v) => {
    setEditId(v.id);
    setForm({ name: v.name, address: v.address || '', sport: v.sport || 'Soccer', pricing: v.pricing || {} });
    setShowAdd(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">{venues.length} Venues</h3>
        <Button size="sm" className="bg-sky-500 hover:bg-sky-600" onClick={() => { setShowAdd(!showAdd); setEditId(null); setForm({ name: '', address: '', sport: 'Soccer', pricing: {} }); }} data-testid="add-venue-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Venue
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-gray-800/60 border-sky-700/30">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-sm">Venue Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="venue-name-input" />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Address</Label>
                <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Sport</Label>
              <select value={form.sport} onChange={e => setForm({ ...form, sport: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2 mt-1 text-sm">
                <option>Soccer</option><option>Gaelic Football</option><option>Hurling/Camogie</option><option>Rugby</option>
              </select>
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Pricing by Fulfillment Location</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {locations.map(loc => (
                  <div key={loc.slug}>
                    <Label className="text-gray-500 text-xs">{loc.name} (${loc.drive_rate_per_hour}/hr)</Label>
                    <Input type="number" step="0.01" placeholder="$0"
                      value={form.pricing[loc.slug] || ''}
                      onChange={e => setForm({ ...form, pricing: { ...form.pricing, [loc.slug]: parseFloat(e.target.value) || 0 } })}
                      className="bg-gray-700 border-gray-600 text-white text-sm" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-green-600" onClick={handleSave} disabled={loading || !form.name}>
                {editId ? 'Update' : 'Create'} Venue
              </Button>
              <Button size="sm" variant="outline" className="border-gray-600 text-gray-400" onClick={() => { setShowAdd(false); setEditId(null); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {venues.map(v => (
          <div key={v.id} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg border border-gray-700 hover:border-gray-600">
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium truncate">{v.name}</div>
              <div className="text-xs text-gray-500">
                {Object.entries(v.pricing || {}).filter(([,p]) => p > 0).map(([loc, price]) => (
                  <span key={loc} className="mr-2">{locLabels[loc] || loc}: <span className="text-green-400">${price}</span></span>
                ))}
              </div>
            </div>
            <div className="flex gap-1 ml-2">
              <button onClick={() => startEdit(v)} className="text-gray-400 hover:text-sky-400 p-1"><Edit className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(v.id)} className="text-gray-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== FULFILLMENT LOCATIONS MANAGEMENT ==============
const FulfillmentLocations = ({ locations, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', address: '', lat: '', lon: '', drive_rate_per_hour: 70 });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const body = {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : null,
        lon: form.lon ? parseFloat(form.lon) : null,
        drive_rate_per_hour: parseFloat(form.drive_rate_per_hour) || 70,
      };
      if (editId) {
        await fetch(`${API}/admin/fulfillment-locations/${editId}`, {
          method: 'PUT', headers: getHeaders(), body: JSON.stringify(body)
        });
      } else {
        await fetch(`${API}/admin/fulfillment-locations`, {
          method: 'POST', headers: getHeaders(), body: JSON.stringify(body)
        });
      }
      setShowAdd(false);
      setEditId(null);
      setForm({ name: '', slug: '', address: '', lat: '', lon: '', drive_rate_per_hour: 70 });
      onRefresh();
    } catch {}
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fulfillment location? Venue pricing referencing this location will become orphaned.')) return;
    await fetch(`${API}/admin/fulfillment-locations/${id}`, { method: 'DELETE', headers: getHeaders() });
    onRefresh();
  };

  const startEdit = (loc) => {
    setEditId(loc.id);
    setForm({
      name: loc.name, slug: loc.slug, address: loc.address || '',
      lat: loc.lat || '', lon: loc.lon || '',
      drive_rate_per_hour: loc.drive_rate_per_hour || 70,
    });
    setShowAdd(true);
  };

  return (
    <div className="space-y-4" data-testid="fulfillment-locations">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">{locations.length} Fulfillment Locations</h3>
        <Button size="sm" className="bg-sky-500 hover:bg-sky-600" onClick={() => { setShowAdd(!showAdd); setEditId(null); setForm({ name: '', slug: '', address: '', lat: '', lon: '', drive_rate_per_hour: 70 }); }} data-testid="add-location-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Location
        </Button>
      </div>

      <p className="text-xs text-gray-500">
        Fulfillment locations are your crew hubs. Addresses are private (never shown to clients). The drive rate determines pricing for venues serviced by this location.
      </p>

      {showAdd && (
        <Card className="bg-gray-800/60 border-sky-700/30">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-gray-300 text-sm">Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Yonkers" className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="location-name-input" />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Slug (unique key)</Label>
                <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })}
                  placeholder="e.g., yonkers" className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="location-slug-input" />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Drive Rate ($/hr)</Label>
                <Input type="number" step="1" value={form.drive_rate_per_hour}
                  onChange={e => setForm({ ...form, drive_rate_per_hour: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="location-rate-input" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Address (private)</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                placeholder="Full street address" className="bg-gray-700 border-gray-600 text-white mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-sm">Latitude</Label>
                <Input type="number" step="0.0001" value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })}
                  placeholder="40.9312" className="bg-gray-700 border-gray-600 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Longitude</Label>
                <Input type="number" step="0.0001" value={form.lon} onChange={e => setForm({ ...form, lon: e.target.value })}
                  placeholder="-73.8987" className="bg-gray-700 border-gray-600 text-white mt-1" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-green-600" onClick={handleSave} disabled={loading || !form.name}>
                {editId ? 'Update' : 'Create'} Location
              </Button>
              <Button size="sm" variant="outline" className="border-gray-600 text-gray-400" onClick={() => { setShowAdd(false); setEditId(null); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {locations.map(loc => (
          <div key={loc.id} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg border border-gray-700 hover:border-gray-600">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-sky-400" />
                <span className="text-white font-medium">{loc.name}</span>
                <Badge variant="outline" className="border-gray-600 text-gray-400 text-xs">{loc.slug}</Badge>
                <Badge className="bg-green-700/50 text-green-300 text-xs">${loc.drive_rate_per_hour}/hr</Badge>
              </div>
              <div className="text-xs text-gray-500 mt-0.5 ml-6">
                <MapPin className="w-3 h-3 inline mr-1" />{loc.address || 'No address set'}
                {loc.lat && loc.lon && <span className="ml-2">({loc.lat.toFixed(4)}, {loc.lon.toFixed(4)})</span>}
              </div>
            </div>
            <div className="flex gap-1 ml-2">
              <button onClick={() => startEdit(loc)} className="text-gray-400 hover:text-sky-400 p-1"><Edit className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(loc.id)} className="text-gray-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== MAIN ADMIN BOOKINGS COMPONENT ==============
const AdminBookings = () => {
  const [locations, setLocations] = useState([]);

  const loadLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/fulfillment-locations`, { headers: getHeaders() });
      setLocations(await res.json());
    } catch {}
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      <h2 className="text-2xl font-bold text-white">Booking Platform</h2>
      <Tabs defaultValue="bookings" className="w-full">
        <TabsList className="bg-gray-800/60 mb-4">
          <TabsTrigger value="bookings" className="data-[state=active]:bg-sky-500 text-sm" data-testid="tab-admin-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="venues" className="data-[state=active]:bg-sky-500 text-sm" data-testid="tab-admin-venues">Venues</TabsTrigger>
          <TabsTrigger value="locations" className="data-[state=active]:bg-emerald-500 text-sm" data-testid="tab-admin-locations">Fulfillment Locations</TabsTrigger>
        </TabsList>
        <TabsContent value="bookings"><BookingManagement /></TabsContent>
        <TabsContent value="venues"><VenueManagement locations={locations} /></TabsContent>
        <TabsContent value="locations"><FulfillmentLocations locations={locations} onRefresh={loadLocations} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminBookings;
